<template>
  <div class="time-selector-wrapper">
    <label v-if="label" :for="timeSelectorId" class="time-selector__label">
      {{ label }}
      <span v-if="required" class="time-selector__required">*</span>
    </label>
    <div
      :id="timeSelectorId"
      ref="triggerRef"
      class="time-selector__trigger"
      :class="{ 'time-selector__trigger--open': isOpen, 'time-selector__trigger--error': !!error }"
      @click="toggleDropdown"
      @blur="handleBlur"
      tabindex="0"
    >
      <span class="time-selector__trigger-text" :class="{ 'is-placeholder': !displayValue }">
        {{ displayValue || "Select Time" }}
      </span>
      <Icon icon="mdi:chevron-down" class="time-selector__trigger-icon" />
    </div>

    <div v-if="error" class="time-selector__helper">
      <span class="time-selector__error">{{ error }}</span>
    </div>

    <Teleport to="body">
      <div v-if="isOpen" class="time-selector" :style="dropdownStyle">
        <div class="time-selector__header">
          <span class="time-selector__header-label">TIME</span>
          <div v-if="loadingSlots" class="time-selector__loading-shimmer"></div>
        </div>

        <div class="time-selector__list" ref="listRef">
          <template v-for="item in allTimes" :key="item.type === 'time' ? item.value : item.label">
            <!-- Section header -->
            <div v-if="item.type === 'header'" class="time-selector__header-item">
              {{ item.label }}
            </div>
            <!-- Time slot -->
            <div
              v-else
              :data-value="item.value"
              class="time-selector__item"
              :class="{
                'time-selector__item--selected': modelValue === item.value,
                'time-selector__item--busy': isBusy(item.value),
              }"
              @click.stop="selectTime(item.value)"
            >
              <span class="time-selector__item-time">{{ item.display }}</span>
              <span v-if="isBusy(item.value)" class="time-selector__item-busy-indicator">●</span>
            </div>
          </template>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script lang="ts" setup>
import { Icon } from "@/components/ui";
import { computed, onMounted, onUnmounted, ref, nextTick } from "vue";

type SlotItem =
  | { type: "time"; value: string; display: string }
  | { type: "header"; label: string };

interface Props {
  modelValue?: string;
  interval?: number;
  startHour?: number;
  label?: string;
  required?: boolean;
  id?: string;
  error?: string;
  busySlots?: string[];
  loadingSlots?: boolean;
  timezone?: string;
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: "",
  interval: 15,
  startHour: 0,
  required: false,
  error: "",
  busySlots: () => [],
  loadingSlots: false,
  timezone: "",
});

const emit = defineEmits<{
  "update:modelValue": [value: string];
  blur: [event: FocusEvent];
}>();

const isOpen = ref(false);
const dropdownStyle = ref<Record<string, string>>({});
const triggerRef = ref<HTMLElement | null>(null);
const listRef = ref<HTMLElement | null>(null);
let clickOutsideHandler: ((event: MouseEvent) => void) | null = null;

const timeSelectorId = computed(
  () => props.id || `time-selector-${Math.random().toString(36).substr(2, 9)}`
);

const isBusy = (timeValue: string): boolean => {
  return props.busySlots?.includes(timeValue) || false;
};

const allTimes = computed((): SlotItem[] => {
  const items: SlotItem[] = [];
  const sections = [
    { label: "Morning", startHour: props.startHour, endHour: 12 },
    { label: "Afternoon", startHour: 12, endHour: 18 },
    { label: "Evening", startHour: 18, endHour: 24 },
  ];

  for (const section of sections) {
    let headerAdded = false;

    for (let h = section.startHour; h < section.endHour && h < 24; h++) {
      for (let m = 0; m < 60; m += props.interval) {
        // Skip if hour is before startHour
        if (h < props.startHour) continue;

        // Add section header before first slot in section
        if (!headerAdded && (h !== props.startHour || m === 0)) {
          items.push({ type: "header", label: section.label });
          headerAdded = true;
        }

        const hours24 = String(h).padStart(2, "0");
        const minutes = String(m).padStart(2, "0");
        const value24 = `${hours24}:${minutes}`;

        const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
        const ampm = h < 12 ? "AM" : "PM";
        const display = `${String(hour12).padStart(2, "0")}:${minutes} ${ampm}`;

        items.push({
          type: "time",
          value: value24,
          display,
        });
      }
    }
  }

  return items;
});

const displayValue = computed(() => {
  if (!props.modelValue) return "";
  const [hours, minutes] = props.modelValue.split(":");
  const h = parseInt(hours, 10);
  const m = parseInt(minutes, 10);

  const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  const ampm = h < 12 ? "AM" : "PM";
  return `${String(hour12).padStart(2, "0")}:${String(m).padStart(2, "0")} ${ampm}`;
});

const selectTime = (value: string) => {
  emit("update:modelValue", value);
  setTimeout(() => {
    isOpen.value = false;
  }, 100);
};

const scrollToSelected = async () => {
  await nextTick();
  if (!listRef.value) return;

  let targetElement: HTMLElement | null = null;

  // Find selected element, or nearest future slot
  if (props.modelValue) {
    targetElement = listRef.value.querySelector(
      `.time-selector__item[data-value="${props.modelValue}"]`
    );
  }

  // If no selection, find nearest future slot (in the selected timezone if provided)
  if (!targetElement) {
    const now = new Date();
    let currentTime: string;

    if (props.timezone) {
      // Format current time in the selected timezone to match busySlots format
      const formatter = new Intl.DateTimeFormat("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
        timeZone: props.timezone,
      });
      currentTime = formatter.format(now);
    } else {
      // Fallback to local timezone if not provided
      const currentHour = String(now.getHours()).padStart(2, "0");
      const currentMin = String(now.getMinutes()).padStart(2, "0");
      currentTime = `${currentHour}:${currentMin}`;
    }

    const allItems = listRef.value.querySelectorAll(".time-selector__item");
    for (const item of allItems) {
      const value = item.getAttribute("data-value");
      if (value && value >= currentTime) {
        targetElement = item as HTMLElement;
        break;
      }
    }
  }

  // Default to first time slot if none found
  if (!targetElement) {
    targetElement = listRef.value.querySelector(".time-selector__item");
  }

  if (targetElement) {
    targetElement.scrollIntoView({ block: "center", behavior: "instant" });
  }
};

const updateDropdownPosition = () => {
  if (!triggerRef.value) return;

  const rect = triggerRef.value.getBoundingClientRect();
  const spacing = 8;
  const dropdownHeight = 250;

  const spaceBelow = window.innerHeight - rect.bottom;
  const spaceAbove = rect.top;
  const shouldShowAbove = spaceBelow < dropdownHeight + spacing && spaceAbove > spaceBelow;

  dropdownStyle.value = {
    position: "fixed",
    top: shouldShowAbove
      ? `${rect.top - dropdownHeight - spacing}px`
      : `${rect.bottom + spacing}px`,
    left: `${rect.left}px`,
    width: `${rect.width}px`,
    zIndex: "10070",
  };
};

const toggleDropdown = () => {
  isOpen.value = !isOpen.value;
  if (isOpen.value) {
    setTimeout(() => {
      updateDropdownPosition();
      scrollToSelected();
    }, 0);
  }
};

const handleBlur = (event: FocusEvent) => {
  const relatedTarget = event.relatedTarget as HTMLElement;
  if (
    relatedTarget &&
    !relatedTarget.closest(".time-selector-wrapper") &&
    !relatedTarget.closest(".time-selector")
  ) {
    setTimeout(() => {
      if (isOpen.value) {
        isOpen.value = false;
      }
      emit("blur", event);
    }, 200);
  } else if (!relatedTarget) {
    setTimeout(() => {
      emit("blur", event);
    }, 100);
  }
};

onMounted(() => {
  clickOutsideHandler = (event: MouseEvent) => {
    const target = event.target as HTMLElement;
    if (target && !target.closest(".time-selector-wrapper") && !target.closest(".time-selector")) {
      isOpen.value = false;
    }
  };
  document.addEventListener("click", clickOutsideHandler);
  window.addEventListener("scroll", updateDropdownPosition, true);
  window.addEventListener("resize", updateDropdownPosition);
});

onUnmounted(() => {
  if (clickOutsideHandler) {
    document.removeEventListener("click", clickOutsideHandler);
  }
  window.removeEventListener("scroll", updateDropdownPosition, true);
  window.removeEventListener("resize", updateDropdownPosition);
});
</script>

<style scoped>
.time-selector-wrapper {
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
}

.time-selector__label {
  display: block;
  font-size: 12px;
  font-weight: 400;
  color: #344054;
  margin-bottom: 6px;
}

.time-selector__required {
  color: #dc3545;
  font-size: 1.1rem;
  margin-left: 2px;
}

.time-selector__trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 8px 12px;
  background-color: #ffffff;
  border: 1px solid #ced4da;
  border-radius: 7px;
  cursor: pointer;
  font-size: 12px;
  min-height: 42px;
  transition: border-color 0.2s ease;
}

.time-selector__trigger:hover {
  border-color: var(--color-border-hover);
}

.time-selector__trigger:focus,
.time-selector__trigger--open {
  outline: none !important;
  border-color: var(--color-primary) !important;
  box-shadow: var(--ring) !important;
}

.time-selector__trigger-text {
  color: #282a30;
  font-weight: 400;
  font-size: 12px;
  flex: 1;
  text-align: left;
}

.time-selector__trigger-text.is-placeholder {
  color: #9ca3af;
  font-size: 12px;
}

.time-selector__trigger-icon {
  font-size: 1rem;
  color: #999;
  transition: transform 0.2s ease;
}

.time-selector {
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid #e4e4e7;
  box-shadow:
    0px 4px 6px -1px rgba(0, 0, 0, 0.1),
    0px 2px 4px -1px rgba(0, 0, 0, 0.06);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  max-height: 250px;
}

.time-selector__header {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  padding: 8px 12px;
  border-bottom: 1px solid #e4e4e7;
}

.time-selector__header-label {
  font-size: 11px;
  font-weight: 600;
  color: #282a30;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.time-selector__list {
  max-height: 200px;
  overflow-y: auto;
  padding: 4px 0;
}

.time-selector__list::-webkit-scrollbar {
  width: 6px;
}

.time-selector__list::-webkit-scrollbar-track {
  background: transparent;
}

.time-selector__list::-webkit-scrollbar-thumb {
  background: #d1d5db;
  border-radius: 3px;
}

.time-selector__list::-webkit-scrollbar-thumb:hover {
  background: #9ca3af;
}

.time-selector__item {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 6px 12px;
  cursor: pointer;
  transition: background-color 0.15s ease;
  color: #282a30;
}

.time-selector__item:hover {
  background-color: #f9fafb;
}

.time-selector__item--selected {
  background-color: #3e3efe;
  color: #ffffff;
}

.time-selector__item--selected:hover {
  background-color: #3535e5;
}

.time-selector__item-time {
  font-size: 12px;
  font-weight: 500;
}

.time-selector__trigger--error {
  border-color: var(--color-danger) !important;
}

.time-selector__helper {
  margin-top: 4px;
  font-size: 0.7rem;
}

.time-selector__error {
  color: var(--color-danger);
}

.time-selector__header-item {
  padding: 8px 12px;
  font-size: 11px;
  font-weight: 600;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background-color: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
  cursor: default;
  user-select: none;
}

.time-selector__item--busy {
  background-color: #fee2e2;
  color: #7f1d1d;
}

.time-selector__item--busy:hover {
  background-color: #fecaca;
}

.time-selector__item-busy-indicator {
  font-size: 12px;
  color: #dc2626;
  margin-left: 6px;
  line-height: 1;
  font-weight: bold;
}

.time-selector__loading-shimmer {
  width: 40px;
  height: 4px;
  background: linear-gradient(90deg, #e5e7eb 25%, #f3f4f6 50%, #e5e7eb 75%);
  background-size: 200% 100%;
  border-radius: 2px;
  animation: shimmer 1.5s infinite;
  margin-left: auto;
}

@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}
</style>
